/**
 * novaDash.ts
 * The air dash — a short, fixed-velocity horizontal burst.
 *
 * Gated on `canDash`, which unlocks at World 5 (ADR-009). While a dash is
 * active it owns Nova's velocity outright: horizontal input and gravity are
 * both suppressed, so the move covers a predictable distance every time. That
 * predictability is the point — a dash whose length depends on how long the
 * player was already running can't be used to clear a known gap.
 *
 * Airborne dashes are limited to one per airborne period rather than by the
 * cooldown alone, so the cooldown can stay short enough to feel responsive on
 * the ground without turning into mid-air flight.
 *
 * @module novaDash
 */

import {
  DASH_SPEED,
  DASH_DURATION_FRAMES,
  DASH_COOLDOWN_FRAMES,
} from '../../constants/gameConstants';
import type { NovaRuntime } from './gameStateTypes';

/**
 * Whether a dash is currently running.
 *
 * @param nova - Nova's runtime state.
 * @returns True while dash frames remain.
 */
export function isDashing(nova: NovaRuntime): boolean {
  return (nova.dashFramesRemaining ?? 0) > 0;
}

/**
 * Advance the dash state machine by one tick.
 *
 * Call before gravity and integration: an active dash overwrites `vy`, and
 * applying gravity afterwards would curve a move that is meant to be flat.
 *
 * @param nova - Nova's runtime state; velocity and dash fields are written.
 * @param dashPressed - Whether the dash button was pressed this tick.
 */
export function stepDash(nova: NovaRuntime, dashPressed: boolean): void {
  if (nova.isGrounded) {
    nova.airDashUsed = false;
  }

  if (!isDashing(nova)) {
    if ((nova.dashCooldownFrames ?? 0) > 0) {
      nova.dashCooldownFrames = (nova.dashCooldownFrames ?? 0) - 1;
    }
    if (!dashPressed || nova.canDash !== true) return;
    if ((nova.dashCooldownFrames ?? 0) > 0) return;
    if (!nova.isGrounded && nova.airDashUsed === true) return;

    nova.dashFramesRemaining = DASH_DURATION_FRAMES;
    nova.dashDirection = nova.facingRight ? 1 : -1;
    if (!nova.isGrounded) {
      nova.airDashUsed = true;
    }
    // Falls through so the launch tick consumes a frame like every other one.
    // DECISION: arming and advancing in the same call keeps the dash exactly
    // DASH_DURATION_FRAMES ticks long. Returning here instead would make it
    // one tick longer than the constant claims.
  }

  const remaining = (nova.dashFramesRemaining ?? 0) - 1;
  nova.dashFramesRemaining = remaining;
  nova.vx = DASH_SPEED * (nova.dashDirection ?? 1);
  // DECISION: hold vy at 0 rather than merely skipping gravity, so entering a
  // dash mid-fall flattens the arc instead of preserving downward speed the
  // player can no longer steer out of.
  nova.vy = 0;
  if (remaining <= 0) {
    nova.dashCooldownFrames = DASH_COOLDOWN_FRAMES;
  }
}
